'use memo'

import {
  AgxButton,
  AgxModal,
  AgxSpacer,
  AgxTypography,
  Icon_AlertCircleOutline,
  Icon_FeaturedIcon,
} from '@urbanx/agx.ui-components2'
import type { TaskStepCompletionConfirmationVariant } from './taskStepConfirmation'
import './TaskStepCompletionConfirmationModal.scss'

interface TaskStepCompletionConfirmationModalProps {
  isOpen: boolean
  taskLabel: string
  variant?: TaskStepCompletionConfirmationVariant
  onCancel: () => void
  onConfirm: () => void
}

const getModalDisplayText = (
  taskLabel: string,
  variant: TaskStepCompletionConfirmationVariant
) => {
  if (variant === 'deposit') {
    return {
      title: `Confirm ${taskLabel} Received`,
      message: `Completing this task will mark the ${taskLabel} as received.`,
    }
  }

  if (variant === 'lastCondition') {
    return {
      title: `Confirm ${taskLabel} Satisfied`,
      message: `Completing this task will mark the ${taskLabel} task as satisfied and the Contract will now transition to Unconditional.`,
    }
  }

  return {
    title: `Confirm ${taskLabel} Satisfied`,
    message: `Completing this task will mark the ${taskLabel} task as satisfied.`,
  }
}

const TaskStepCompletionConfirmationModal = ({
  isOpen,
  taskLabel,
  variant,
  onCancel,
  onConfirm,
}: TaskStepCompletionConfirmationModalProps) => {
  if (!isOpen || !variant) return null

  const { title, message } = getModalDisplayText(taskLabel, variant)
  const actionButtons = (
    <AgxSpacer
      className="taskStepConfirmationModal-actions"
      direction="horizontal"
      gap="spacing-xl"
      contentJustification="center"
      fill
    >
      <AgxButton
        id="cancel-step-completion"
        text="Cancel"
        variant="hollow"
        size="large"
        onClick={onCancel}
        stretch
      />
      <AgxButton
        id="confirm-step-completion"
        text="Confirm"
        variant="primary"
        size="large"
        onClick={onConfirm}
        stretch
      />
    </AgxSpacer>
  )

  return (
    <AgxModal
      id="confirm-step-completion-modal"
      isOpen
      title={title}
      type="single"
      variant="withoutDivider"
      actionButtonContainerStyle="withDivider"
      maxWidth="600px"
      setIsDialogOpen={(nextIsOpen) => {
        if (!nextIsOpen) onCancel()
      }}
      titleIcon={
        <Icon_FeaturedIcon
          icon={<Icon_AlertCircleOutline type="Danger" />}
          color="var(--text-error)"
          backgroundColor="var(--bg-error-light)"
        />
      }
      content={
        <div className="taskStepConfirmationModal-content">
          <AgxTypography
            id="confirm-step-completion-message"
            type="body"
            size="medium"
            color="var(--text-secondary)"
          >
            {message}
            <br />
            This action cannot be undone. Do you wish to continue?
          </AgxTypography>
        </div>
      }
      actionButtons={actionButtons}
    />
  )
}

export default TaskStepCompletionConfirmationModal
